import { AlertTriangle, Info, Loader2 } from 'lucide-react';
import { useState } from 'react';

import type {
  GapAnalysisProduct,
  GapAnalysisSummary,
} from '~/features/catalog-operations/types/catalog-operations.types';
import { cn } from '~/utils/cn';

import {
  DEMAND_ROW_LIMIT,
  EMPTY_GAP_FILTERS,
  applyGapFilters,
  countRows,
  hasActiveFilters,
  hasKeywordData,
  toAspectRollups,
  toTopDemandRows,
  volumeStatusNote,
} from './gap-analysis.utils';
import { GapAttributeDiff } from './gap-attribute-diff';
import { GapAttributeTable } from './gap-attribute-table';
import { GapComparisonRails } from './gap-comparison-rails';
import { GapCoveragePanel } from './gap-coverage-panel';
import { GapDemandPanel } from './gap-demand-panel';
import { GapFilterBar } from './gap-filter-bar';
import { GapSummaryStats } from './gap-summary-stats';

interface Props {
  /** Mapped content-delta gap analysis for the product, or undefined while the
   *  request is still in flight / has failed. */
  gap: GapAnalysisProduct | undefined;
}

/** Card shell for the states that carry no data. */
function GapShell({ children, testId }: { children: React.ReactNode; testId: string }) {
  return (
    <div className="overflow-hidden rounded-2xl border bg-white p-5 shadow-sm" data-testid={testId}>
      {children}
    </div>
  );
}

/** Centred message for the two empty shapes this endpoint can legitimately return. */
function GapEmpty({ title, body }: { title: string; body?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 py-24 text-center">
      <p
        className="text-foreground text-sm leading-5 font-semibold"
        data-testid="gap-analysis-empty-title"
      >
        {title}
      </p>
      {body ? <p className="text-muted-foreground max-w-md text-sm leading-5">{body}</p> : null}
    </div>
  );
}

/** Extraction issues mean every count below under-represents the product, so
 *  this is the one caveat promoted above the data rather than filed under it. */
function GapIssuesBanner({ issues }: { issues: string[] }) {
  return (
    <div
      className="border-error/30 bg-error-50 text-foreground m-5 mb-0 flex items-start gap-3 rounded-lg border p-3.5 text-sm leading-5"
      data-testid="gap-analysis-issues-banner"
    >
      <AlertTriangle className="text-error mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <span>
        <span className="font-semibold">
          Part of this product&apos;s content could not be read.
        </span>{' '}
        We hit: {issues.join('; ')}. Every count below is lower than the real figure — refresh
        before sharing these numbers.
      </span>
    </div>
  );
}

/** The caveats that qualify how the figures were produced. They sit above the
 *  data, so nobody reads a number here without the qualification that applies
 *  to it. Kept small and muted so they inform without competing. */
function GapNotes({ summary }: { summary: GapAnalysisSummary }) {
  const volumeNote = volumeStatusNote(summary.volumeStatus);

  return (
    <div
      className="text-muted-foreground bg-muted/30 flex flex-col gap-1.5 border-b px-5 py-3.5 text-xs leading-5"
      data-testid="gap-analysis-notes"
    >
      {volumeNote ? (
        <div className="flex items-start gap-2" data-testid="gap-analysis-volume-note-banner">
          <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          <span>
            {volumeNote} This affects the search figures only — the new vs restated split still
            holds.
          </span>
        </div>
      ) : null}
      <div className="flex items-start gap-2">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span>
          An attribute is <span className="text-foreground font-semibold">restated</span> when it
          means close to the same thing as something the product already said before enrichment. The
          similarity cut-off
          {summary.similarityThreshold != null ? (
            <>
              {' '}
              sits at{' '}
              <span
                className="text-foreground font-semibold"
                data-testid="gap-analysis-similarity-threshold-value"
              >
                {summary.similarityThreshold}
              </span>
            </>
          ) : null}{' '}
          and has not been re-tuned for the model in use today, so treat borderline calls as a guide
          rather than a verdict.
        </span>
      </div>
    </div>
  );
}

/**
 * Gap Analysis tab — a content delta, not a quality judgement. Answers "what was
 * missing before enrichment, and what did enrichment add?" by showing the
 * retailer's own attributes beside the enriched ones, with the rollups that make
 * the size of the change and its search value readable at a glance.
 */
export function GapAnalysis({ gap }: Props) {
  // Declared above the status branches below — hooks cannot sit after an early
  // return, and every branch other than the populated one renders no filters.
  const [filters, setFilters] = useState(EMPTY_GAP_FILTERS);

  // Read-through: a first read starts the agent and answers PENDING, so this is
  // "being computed", not "absent". The hook polls; the UI just says so.
  if (gap && (gap.status === 'PENDING' || gap.status === 'RUNNING')) {
    return (
      <GapShell testId="gap-analysis-loading">
        <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
          <Loader2 className="text-primary size-5 animate-spin" aria-hidden="true" />
          <p className="text-foreground text-sm leading-5 font-semibold">Comparing this product…</p>
          <p className="text-muted-foreground max-w-md text-sm leading-5">
            The comparison runs the first time a product is opened. This page updates itself when it
            is ready.
          </p>
        </div>
      </GapShell>
    );
  }

  // FAILED carries a reason and is not retried automatically — the refresh
  // control is the documented way out, so it has to be reachable here.
  if (gap?.status === 'FAILED') {
    return (
      <GapShell testId="gap-analysis-error">
        <div className="flex flex-col items-center justify-center gap-2 py-20 text-center">
          <AlertTriangle className="text-error size-5" aria-hidden="true" />
          <p className="text-foreground text-sm leading-5 font-semibold">
            The comparison could not be completed.
          </p>
          <p
            className="text-muted-foreground max-w-md text-sm leading-5"
            data-testid="gap-analysis-error-message"
          >
            {gap.error ??
              'It stopped without reporting a reason. Use the refresh control in the page header to try again.'}
          </p>
        </div>
      </GapShell>
    );
  }

  // COMPLETED with nothing persisted — gap analysis was never enabled for the run.
  if (!gap || (gap.summary === null && gap.sections.length === 0)) {
    return (
      <GapShell testId="gap-analysis-not-compared-empty">
        <GapEmpty
          title="This product has not been compared yet."
          body="The comparison is an optional step and was not enabled for this run. Use the refresh control in the page header to run it now."
        />
      </GapShell>
    );
  }

  // Summary present but no sections — a real "no distinct attributes" answer,
  // not a failure, so it must not read as an error.
  if (gap.sections.length === 0) {
    return (
      <GapShell testId="gap-analysis-no-attributes-empty">
        <GapEmpty
          title="No attributes to compare."
          body="We compared this product but could not identify separate attributes before or after enrichment."
        />
      </GapShell>
    );
  }

  const rollups = toAspectRollups(gap.sections);
  // No keyword match anywhere means the search panel has nothing to rank. It is
  // dropped rather than shown empty — why the figures are missing is already
  // covered once by the volume note at the foot of the tab.
  const demandRows = toTopDemandRows(rollups, DEMAND_ROW_LIMIT);
  const showKeywordFilters = hasKeywordData(gap.sections);

  // Filters drive the comparison and the table only. The stat tiles and both
  // charts keep describing the whole product, since those are the figures quoted
  // for it — narrowing them would misrepresent what enrichment did.
  const filteredRollups = applyGapFilters(rollups, filters);
  const isFiltered = hasActiveFilters(filters);

  function clearFilters() {
    setFilters((current) => ({ ...EMPTY_GAP_FILTERS, sort: current.sort }));
  }

  function toggleCategory(category: string) {
    setFilters((current) => ({
      ...current,
      categories: current.categories.includes(category)
        ? current.categories.filter((entry) => entry !== category)
        : [...current.categories, category],
    }));
  }

  return (
    <div
      className="overflow-hidden rounded-2xl border bg-white shadow-sm"
      data-testid="gap-analysis-panel"
    >
      {gap.summary ? (
        <>
          {gap.summary.issues.length > 0 ? <GapIssuesBanner issues={gap.summary.issues} /> : null}
          <GapNotes summary={gap.summary} />
          <GapSummaryStats summary={gap.summary} rollups={rollups} />
        </>
      ) : null}

      <div className={cn('grid grid-cols-1', demandRows.length > 0 && 'lg:grid-cols-2')}>
        <GapCoveragePanel
          rollups={rollups}
          hasSibling={demandRows.length > 0}
          selectedCategories={filters.categories}
          onToggleCategory={toggleCategory}
        />
        {demandRows.length > 0 ? <GapDemandPanel rows={demandRows} /> : null}
      </div>

      {gap.summary ? <GapComparisonRails summary={gap.summary} /> : null}

      <GapAttributeDiff
        rollups={filteredRollups}
        isFiltered={isFiltered}
        onClearFilters={clearFilters}
        filterBar={
          <GapFilterBar
            testId="gap-analysis-filter"
            rollups={rollups}
            filters={filters}
            onChange={setFilters}
            showKeywordFilters={showKeywordFilters}
          />
        }
      />

      <GapAttributeTable
        rollups={filteredRollups}
        showKeywordColumns={showKeywordFilters}
        totalRows={countRows(rollups)}
        isFiltered={isFiltered}
        onClearFilters={clearFilters}
      />
    </div>
  );
}
